import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Button, TextField, Chip, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Snackbar, Alert, Grid, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip
} from '@mui/material';
import { Download, CheckCircle, Cancel, Receipt, Info } from '@mui/icons-material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, Legend } from 'recharts';

const DoctorPaymentDetailPage = () => {
  const { adminUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const { doctorId } = useParams();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [doctorDetails, setDoctorDetails] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [filters, setFilters] = useState({ date: '', transactionId: '', status: 'all', sortBy: 'date', sortOrder: 'desc' });
  const [actionDialog, setActionDialog] = useState({
    open: false,
    withdrawalId: null,
    action: '',
    paymentDetails: { transactionId: '', paymentMode: '', paidAmount: '', dateOfPayment: '' },
    reason: ''
  });

  useEffect(() => {
    if (!adminUser || !adminUser.accessToken) {
      navigate('/admin-login');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
      try {
        const [doctorRes, earningsRes, withdrawalsRes] = await Promise.all([
          api.get(`/admin/doctor-payment-details/${doctorId}`, config),
          api.get('/admin/earnings-payments', config),
          api.get(`/admin/withdrawals/doctor/${doctorId}`, config),
        ]);

        setDoctorDetails(doctorRes.data);

        const doctorEarnings = earningsRes.data.breakdown.filter(e => e.doctorId === doctorId);
        const aggregatedEarnings = doctorEarnings.reduce((acc, e) => {
          acc.totalPaid += e.totalPaid || 0;
          acc.doctorFee += e.doctorFee || 0;
          acc.patientCommission += e.patientCommission || 0;
          acc.doctorCommission += e.doctorCommission || 0;
          acc.finalDoctorEarning += e.finalDoctorEarning || 0;
          acc.appointmentIds = [...acc.appointmentIds, ...e.appointmentIds];
          return acc;
        }, {
          totalPaid: 0,
          doctorFee: 0,
          patientCommission: 0,
          doctorCommission: 0,
          finalDoctorEarning: 0,
          appointmentIds: []
        });

        const totalWithdrawn = withdrawalsRes.data
          .filter(w => w.status === 'approved')
          .reduce((sum, w) => sum + (w.approvedAmount || w.amount), 0);
        aggregatedEarnings.totalWithdrawn = totalWithdrawn;
        aggregatedEarnings.pendingEarnings = aggregatedEarnings.finalDoctorEarning - totalWithdrawn;

        setEarnings(aggregatedEarnings);
        setWithdrawals(withdrawalsRes.data || []);
      } catch (err) {
        console.error('Fetch error:', err.response?.data || err.message);
        setSnackbar({ open: true, message: 'Failed to fetch doctor details', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [adminUser, navigate, doctorId]);

  const filteredWithdrawals = withdrawals
    .filter(w => {
      const dateMatch = !filters.date || new Date(w.requestDate).toISOString().split('T')[0] === filters.date;
      const transactionIdMatch = !filters.transactionId || w.transactionId?.includes(filters.transactionId);
      const statusMatch = filters.status === 'all' || w.status === filters.status;
      return dateMatch && transactionIdMatch && statusMatch;
    })
    .sort((a, b) => {
      const order = filters.sortOrder === 'asc' ? 1 : -1;
      if (filters.sortBy === 'date') {
        return order * (new Date(a.requestDate) - new Date(b.requestDate));
      } else if (filters.sortBy === 'amount') {
        return order * (a.amount - b.amount);
      }
      return 0;
    });

  const paymentDistributionData = [
    { name: 'Final Doctor Earning', value: earnings?.finalDoctorEarning || 0 },
    { name: 'Total Amount Available for Withdrawal', value: earnings?.pendingEarnings || 0 },
    { name: 'Total Withdrawn', value: earnings?.totalWithdrawn || 0 },
  ];
  const COLORS = ['#0088FE', '#FFBB28', '#00C49F'];

  const PAYMENT_MODES = ['Bank Transfer', 'UPI', 'Paytm', 'Other'];

  const handleApproveWithdrawal = async () => {
    const { withdrawalId, paymentDetails } = actionDialog;
    const { transactionId, paymentMode, paidAmount, dateOfPayment } = paymentDetails;

    if (!transactionId || !paymentMode || !paidAmount || !dateOfPayment) {
      setSnackbar({ open: true, message: 'All payment details are required', severity: 'error' });
      return;
    }
    if (!PAYMENT_MODES.includes(paymentMode)) {
      setSnackbar({ open: true, message: `Invalid payment mode. Must be one of: ${PAYMENT_MODES.join(', ')}`, severity: 'error' });
      return;
    }
    if (isNaN(paidAmount) || parseFloat(paidAmount) <= 0) {
      setSnackbar({ open: true, message: 'Paid amount must be a positive number', severity: 'error' });
      return;
    }

    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const response = await api.put(`/admin/withdrawals/approve/${withdrawalId}`, {
        transactionId,
        paymentMode,
        paidAmount: parseFloat(paidAmount),
        dateOfPayment,
      }, config);

      const updatedWithdrawal = response.data.withdrawal;
      setWithdrawals(withdrawals.map(w => w._id === withdrawalId ? updatedWithdrawal : w));
      setEarnings(prev => ({
        ...prev,
        totalWithdrawn: (prev.totalWithdrawn || 0) + parseFloat(paidAmount),
        pendingEarnings: (prev.pendingEarnings || 0) - parseFloat(paidAmount),
      }));

      setActionDialog({ ...actionDialog, open: false, paymentDetails: { transactionId: '', paymentMode: '', paidAmount: '', dateOfPayment: '' } });
      setSnackbar({ open: true, message: 'Withdrawal approved and receipt uploaded', severity: 'success' });
    } catch (err) {
      console.error('Approval error:', err.response?.data || err);
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to approve withdrawal', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRejectWithdrawal = async () => {
    const { withdrawalId, reason } = actionDialog;
    if (!reason) {
      setSnackbar({ open: true, message: 'Please provide a rejection reason', severity: 'error' });
      return;
    }
    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      await api.put(`/admin/withdrawals/reject/${withdrawalId}`, { reason }, config);
      setWithdrawals(withdrawals.map(w => w._id === withdrawalId ? { ...w, status: 'rejected', rejectionReason: reason, dateOfRejection: new Date().toISOString() } : w));
      setActionDialog({ ...actionDialog, open: false, reason: '' });
      setSnackbar({ open: true, message: 'Withdrawal rejected', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to reject withdrawal', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (withdrawalId) => {
    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const res = await api.get(`/admin/withdrawals/invoice/${withdrawalId}`, config);
      window.open(res.data.url, '_blank'); // Open the B2 URL in a new tab
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to download invoice', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async (withdrawalId) => {
    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const res = await api.get(`/admin/withdrawals/receipt/${withdrawalId}`, config);
      window.open(res.data.url, '_blank'); // Open the B2 URL in a new tab
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to download receipt', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  if (loading || !doctorDetails || !earnings) return <CircularProgress />;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Doctor Payment Details</Typography>
      <Paper sx={{ p: 2 }}>
        {/* Doctor Profile */}
        <Typography variant="h6" gutterBottom>Doctor Profile</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}><Typography><strong>Name:</strong> {doctorDetails.name || 'N/A'}</Typography></Grid>
          <Grid item xs={12} sm={4}><Typography><strong>Email:</strong> {doctorDetails.email || 'N/A'}</Typography></Grid>
          <Grid item xs={12} sm={4}><Typography><strong>Phone:</strong> {doctorDetails.phone || 'N/A'}</Typography></Grid>
          <Grid item xs={12} sm={4}><Typography><strong>Specialization:</strong> {doctorDetails.specialization || 'N/A'}</Typography></Grid>
          <Grid item xs={12} sm={4}><Typography><strong>Experience:</strong> {doctorDetails.experience || 'N/A'} years</Typography></Grid>
          <Grid item xs={12} sm={4}><Typography><strong>Address:</strong> {doctorDetails.address || 'N/A'}</Typography></Grid>
          <Grid item xs={12} sm={4}><Typography><strong>City:</strong> {doctorDetails.city || 'N/A'}</Typography></Grid>
          <Grid item xs={12} sm={4}><Typography><strong>State:</strong> {doctorDetails.state || 'N/A'}</Typography></Grid>
          <Grid item xs={12} sm={4}><Typography><strong>Consultation Fee:</strong> ₹{doctorDetails.consultationFee || 'N/A'}</Typography></Grid>
        </Grid>

        {/* Bank Details */}
        <Typography variant="h6" gutterBottom>Bank Details</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}><Typography><strong>Account Holder Name:</strong> {doctorDetails.bankDetails?.accountHolderName || 'N/A'}</Typography></Grid>
          <Grid item xs={12} sm={4}><Typography><strong>Account Number:</strong> {doctorDetails.bankDetails?.accountNumber || 'N/A'}</Typography></Grid>
          <Grid item xs={12} sm={4}><Typography><strong>Bank Name:</strong> {doctorDetails.bankDetails?.bankName || 'N/A'}</Typography></Grid>
          <Grid item xs={12} sm={4}><Typography><strong>Branch Name:</strong> {doctorDetails.bankDetails?.branchName || 'N/A'}</Typography></Grid>
          <Grid item xs={12} sm={4}><Typography><strong>IFSC Code:</strong> {doctorDetails.bankDetails?.ifscCode || 'N/A'}</Typography></Grid>
          <Grid item xs={12} sm={4}><Typography><strong>UPI ID:</strong> {doctorDetails.bankDetails?.upiId || 'N/A'}</Typography></Grid>
          <Grid item xs={12} sm={4}><Typography><strong>Google Pay Number:</strong> {doctorDetails.bankDetails?.googlePayNumber || 'N/A'}</Typography></Grid>
          <Grid item xs={12} sm={4}><Typography><strong>Paytm Number:</strong> {doctorDetails.bankDetails?.paytmNumber || 'N/A'}</Typography></Grid>
          <Grid item xs={12} sm={4}><Typography><strong>PhonePe Number:</strong> {doctorDetails.bankDetails?.phonePeNumber || 'N/A'}</Typography></Grid>
        </Grid>

        {/* Earnings Summary */}
        <Typography variant="h6" gutterBottom>
          Earnings Summary
          <Tooltip title="Earnings breakdown showing Total Paid, Doctor Fee, Commissions, and Final Earnings, consistent with Payment Management.">
            <IconButton size="small"><Info fontSize="small" /></IconButton>
          </Tooltip>
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}><Typography><strong>Total Paid:</strong> ₹{earnings.totalPaid.toFixed(2)}</Typography></Grid>
          <Grid item xs={12} sm={3}><Typography><strong>Doctor Fee:</strong> ₹{earnings.doctorFee.toFixed(2)}</Typography></Grid>
          <Grid item xs={12} sm={3}><Typography><strong>Patient Commission:</strong> ₹{earnings.patientCommission.toFixed(2)}</Typography></Grid>
          <Grid item xs={12} sm={3}><Typography><strong>Doctor Commission:</strong> ₹{earnings.doctorCommission.toFixed(2)}</Typography></Grid>
          <Grid item xs={12} sm={3}><Typography><strong>Final Doctor Earning:</strong> ₹{earnings.finalDoctorEarning.toFixed(2)}</Typography></Grid>
          <Grid item xs={12} sm={3}><Typography><strong>Total Withdrawn:</strong> ₹{earnings.totalWithdrawn.toFixed(2)}</Typography></Grid>
          <Grid item xs={12} sm={3}><Typography><strong>Total Amount Available for Withdrawal:</strong> ₹{earnings.pendingEarnings.toFixed(2)}</Typography></Grid>
        </Grid>

        {/* Payment Distribution Chart */}
        <Typography variant="h6" gutterBottom>
          Payment Distribution
          <Tooltip title="Chart showing Final Doctor Earning, Total Amount Available for Withdrawal, and Total Withdrawn, consistent with earnings data.">
            <IconButton size="small"><Info fontSize="small" /></IconButton>
          </Tooltip>
        </Typography>
        <PieChart width={300} height={200}>
          <Pie data={paymentDistributionData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} fill="#8884d8">
            {paymentDistributionData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
          </Pie>
          <RechartsTooltip />
          <Legend />
        </PieChart>

        {/* Past Withdrawal History */}
        <Typography variant="h6" gutterBottom>Past Withdrawal History</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={3}>
            <TextField type="date" label="Filter by Date" value={filters.date} onChange={e => setFilters({ ...filters, date: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Filter by Transaction ID" value={filters.transactionId} onChange={e => setFilters({ ...filters, transactionId: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select value={filters.sortBy} onChange={e => setFilters({ ...filters, sortBy: e.target.value })}>
                <MenuItem value="date">Date</MenuItem>
                <MenuItem value="amount">Amount</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Sort Order</InputLabel>
              <Select value={filters.sortOrder} onChange={e => setFilters({ ...filters, sortOrder: e.target.value })}>
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Request Date & Time</TableCell>
              <TableCell>Withdrawal ID</TableCell>
              <TableCell>Requested Amount</TableCell>
              <TableCell>Approved Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Transaction ID</TableCell>
              <TableCell>Payment Mode</TableCell>
              <TableCell>Date & Time of Payment/Rejection</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredWithdrawals.map(w => (
              <TableRow key={w._id}>
                <TableCell>{new Date(w.requestDate).toLocaleString()}</TableCell>
                <TableCell>{w._id}</TableCell>
                <TableCell>₹{w.amount}</TableCell>
                <TableCell>₹{w.approvedAmount || 0}</TableCell>
                <TableCell><Chip label={w.status} color={w.status === 'approved' ? 'success' : w.status === 'rejected' ? 'error' : 'warning'} /></TableCell>
                <TableCell>{w.transactionId || 'N/A'}</TableCell>
                <TableCell>{w.paymentMode || 'N/A'}</TableCell>
                <TableCell>
                  {w.status === 'approved' && w.dateOfPayment
                    ? new Date(w.dateOfPayment).toLocaleString()
                    : w.status === 'rejected' && w.dateOfRejection
                    ? new Date(w.dateOfRejection).toLocaleString()
                    : 'N/A'}
                </TableCell>
                <TableCell>
                  {w.status === 'pending' && (
                    <>
                      <Tooltip title="Approve">
                        <IconButton onClick={() => setActionDialog({ 
                          open: true, 
                          withdrawalId: w._id, 
                          action: 'approve', 
                          paymentDetails: { 
                            transactionId: '', 
                            paymentMode: '', 
                            paidAmount: w.amount.toString(),
                            dateOfPayment: new Date().toISOString().split('T')[0] 
                          } 
                        })}>
                          <CheckCircle color="success" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton onClick={() => setActionDialog({ open: true, withdrawalId: w._id, action: 'reject', paymentDetails: {} })}>
                          <Cancel color="error" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {w.status === 'approved' && (
                    <>
                      <Tooltip title="Download Invoice">
                        <IconButton onClick={() => handleDownloadInvoice(w._id)}>
                          <Download />
                        </IconButton>
                      </Tooltip>
                      {w.receiptPath && (
                        <Tooltip title="Download Receipt">
                          <IconButton onClick={() => handleDownloadReceipt(w._id)}>
                            <Receipt />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onClose={() => setActionDialog({ ...actionDialog, open: false })}>
        <DialogTitle>{actionDialog.action === 'approve' ? 'Approve Withdrawal' : 'Reject Withdrawal'}</DialogTitle>
        <DialogContent>
          {actionDialog.action === 'approve' ? (
            <>
              <Typography>Enter payment details to approve this withdrawal:</Typography>
              <TextField 
                label="Transaction ID" 
                value={actionDialog.paymentDetails.transactionId} 
                onChange={e => setActionDialog({ ...actionDialog, paymentDetails: { ...actionDialog.paymentDetails, transactionId: e.target.value } })} 
                fullWidth 
                sx={{ mt: 2 }} 
                required
              />
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Payment Mode</InputLabel>
                <Select
                  value={actionDialog.paymentDetails.paymentMode}
                  onChange={e => setActionDialog({ ...actionDialog, paymentDetails: { ...actionDialog.paymentDetails, paymentMode: e.target.value } })}
                  label="Payment Mode"
                  required
                >
                  {PAYMENT_MODES.map(mode => (
                    <MenuItem key={mode} value={mode}>{mode}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField 
                label="Paid Amount" 
                type="number" 
                value={actionDialog.paymentDetails.paidAmount} 
                onChange={e => setActionDialog({ ...actionDialog, paymentDetails: { ...actionDialog.paymentDetails, paidAmount: e.target.value } })} 
                fullWidth 
                sx={{ mt: 2 }} 
                required
              />
              <TextField 
                label="Date of Payment" 
                type="date" 
                value={actionDialog.paymentDetails.dateOfPayment} 
                onChange={e => setActionDialog({ ...actionDialog, paymentDetails: { ...actionDialog.paymentDetails, dateOfPayment: e.target.value } })} 
                fullWidth 
                sx={{ mt: 2 }} 
                InputLabelProps={{ shrink: true }} 
                required
              />
            </>
          ) : (
            <TextField 
              label="Rejection Reason" 
              multiline 
              rows={3} 
              value={actionDialog.reason} 
              onChange={e => setActionDialog({ ...actionDialog, reason: e.target.value })} 
              fullWidth 
              sx={{ mt: 2 }} 
              required
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ ...actionDialog, open: false })} disabled={loading}>Cancel</Button>
          <Button 
            onClick={actionDialog.action === 'approve' ? handleApproveWithdrawal : handleRejectWithdrawal} 
            color={actionDialog.action === 'approve' ? 'success' : 'error'} 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : actionDialog.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default DoctorPaymentDetailPage;